import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, fmtIQD } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RequireAuth from "../components/RequireAuth";
import { RefreshCw, Package } from "lucide-react";

const STATUS_LABEL = {
  new: "طلب جديد",
  confirmed: "تم تأكيد الطلب",
  preparing: "قيد التجهيز",
  out_for_delivery: "خرج للتوصيل",
  completed: "تم التسليم",
  cancelled: "ملغي",
};
const STATUS_STYLE = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  confirmed: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  preparing: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  out_for_delivery: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  completed: "bg-green-500/15 text-green-300 border-green-500/40",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
};

export default function Account() {
  return <RequireAuth><AccountInner /></RequireAuth>;
}

function AccountInner() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await api.get("/orders/my");
      setOrders(r.data);
      setError(false);
    } catch { setError(true); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling: 15s while visible; refetch on window focus; pause when hidden; cleanup on unmount
  useEffect(() => {
    const startPoll = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(() => load(true), 15000);
    };
    const stopPoll = () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    const onVisibility = () => {
      if (document.hidden) stopPoll();
      else { load(true); startPoll(); }
    };
    const onFocus = () => load(true);

    if (!document.hidden) startPoll();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      stopPoll();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  return (
    <div className="App">
      <Header />
      <div className="pg-page-top pb-16">
        <div className="pg-container">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <div>
              <h1 className="text-3xl md:text-4xl font-black">مرحباً {user.full_name}</h1>
              <p className="text-gray-400 text-sm mt-1">{user.phone}</p>
            </div>
            <button data-testid="account-logout" onClick={async () => { await logout(); nav("/"); }} className="text-sm text-[#EF1B17] font-bold">تسجيل الخروج</button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-black">طلباتي ({orders.length})</h2>
            <button data-testid="orders-refresh" onClick={() => load(false)} disabled={refreshing}
              className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-[#EF1B17] border border-[#3A3D43] hover:border-[#EF1B17] rounded-md px-3 py-1.5 disabled:opacity-50">
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> تحديث
            </button>
          </div>

          {loading ? (
            <div className="space-y-3">
              <div className="pg-skeleton h-24 rounded-lg" />
              <div className="pg-skeleton h-24 rounded-lg" />
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
              <p className="text-red-400 mb-3">تعذّر تحميل الطلبات</p>
              <button onClick={() => load(false)} className="text-sm text-[#EF1B17] font-bold">إعادة المحاولة</button>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16 bg-[#24262B] border border-[#3A3D43] rounded-lg">
              <Package size={48} className="mx-auto text-[#3A3D43] mb-3" />
              <p className="text-gray-400">لا توجد طلبات بعد.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-${o.id}`} className="bg-[#24262B] border border-[#3A3D43] rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-black">{o.order_number}</span>
                        <span data-testid={`order-status-${o.id}`} className={`text-xs font-bold px-2 py-0.5 rounded border ${STATUS_STYLE[o.status] || "bg-gray-500/15 text-gray-300 border-gray-500/40"}`}>
                          {STATUS_LABEL[o.status] || o.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(o.created_at).toLocaleDateString("ar-IQ-u-nu-latn")} • {o.items.length} منتج
                        {o.updated_at && <> • آخر تحديث: {new Date(o.updated_at).toLocaleString("ar-IQ-u-nu-latn")}</>}
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="text-[#EF1B17] font-black text-lg">{fmtIQD(o.total)}</div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-[#3A3D43]/60 grid gap-1 text-xs text-gray-300">
                    <div className="flex justify-between"><span className="text-gray-500">المنتجات</span><span>{fmtIQD(o.subtotal || 0)}</span></div>
                    {(o.discount ?? 0) > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>خصم كود {o.coupon?.code || ""}</span><span>-{fmtIQD(o.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between"><span className="text-gray-500">التوصيل</span><span>{(o.delivery_fee ?? 0) === 0 ? "مجاني" : fmtIQD(o.delivery_fee)}</span></div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-[#3A3D43]/60 space-y-1">
                    {o.items.slice(0, 3).map((it) => (
                      <div key={it.id} className="text-xs text-gray-300 flex justify-between gap-2">
                        <span className="line-clamp-1">{it.product_name}{it.variant_name ? ` — ${it.variant_name}` : ""} × {it.quantity}</span>
                        <span className="shrink-0">{fmtIQD(it.line_total)}</span>
                      </div>
                    ))}
                    {o.items.length > 3 && <div className="text-[10px] text-gray-500">+{o.items.length - 3} منتجات إضافية</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
